<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div class="dashboard-header assona-header">
        <div>
          <h1 class="dashboard-title">Assona24H</h1>
          <p class="dashboard-subtitle">
            Übersicht der Assona-24H-Tickets.
          </p>
        </div>
        <button type="button" class="btn btn-primary" @click="openCreate">
          Reparatur hinzufügen
        </button>
      </div>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>

      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <div v-else-if="allCount === 0" class="empty-state">
        Noch keine Assona24H-Tickets vorhanden.
      </div>

      <template v-else>
        <div class="assona-filters" role="group" aria-label="Status-Filter">
          <button
            type="button"
            class="assona-filter"
            :class="{ active: activeStatusId === null }"
            :style="activeStatusId === null ? alleActiveStyle : undefined"
            @click="setFilter(null)"
          >
            Alle
            <span class="assona-filter-count">{{ allCount }}</span>
          </button>
          <button
            v-for="s in statusCounts"
            :key="s.status_id"
            type="button"
            class="assona-filter"
            :class="{ active: activeStatusId === s.status_id }"
            :style="activeStatusId === s.status_id ? statusStyle(s) : undefined"
            @click="setFilter(s.status_id)"
          >
            {{ s.status }}
            <span class="assona-filter-count">{{ s.count }}</span>
          </button>
        </div>

        <div class="assona-search">
          <div class="input-group">
            <input
              ref="searchInput"
              v-model="searchInputValue"
              type="search"
              class="form-control"
              placeholder="Suche …"
              aria-label="Tickets durchsuchen"
              autocomplete="off"
              enterkeyhint="search"
              @keydown.enter.prevent="applySearch"
              @search="applySearch"
            />
            <button
              type="button"
              class="btn btn-outline-secondary assona-scan-btn"
              title="Barcode scannen"
              aria-label="Barcode scannen"
              @click="openScanner"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 5v14M7 5v14M11 5v14M14 5v14M18 5v14M21 5v14" />
              </svg>
            </button>
          </div>
          <p v-if="pageLoading" class="assona-search-meta">
            {{ query ? 'Suche läuft …' : 'Lädt …' }}
          </p>
          <p v-else-if="pageError" class="error">{{ pageError }}</p>
          <p v-else-if="query" class="assona-search-meta">
            {{ total }} Treffer für „{{ query }}“
            <button type="button" class="btn btn-link btn-sm assona-clear" @click="clearSearch">
              zurücksetzen
            </button>
          </p>
        </div>

        <!-- Während des Ladens und nach einem Fehler steht die Meldung oben; die
             Liste bleibt so lange weg, statt den alten Stand zu behaupten. -->
        <div
          v-if="!pageLoading && !pageError && tickets.length === 0"
          class="empty-state"
        >
          <template v-if="query">Keine Tickets für „{{ query }}“ gefunden.</template>
          <template v-else>Keine Tickets mit Status „{{ activeStatusName }}“.</template>
        </div>

        <div v-else-if="!pageLoading && !pageError" class="ticket-section assona-tablecard">
          <table class="table table-sm table-striped align-middle assona-table">
            <thead>
              <tr>
                <th scope="col" style="width: 200px;">Ticket-ID</th>
                <th scope="col" style="width: 180px;">Erstellt</th>
                <th scope="col">Status</th>
                <th scope="col" style="width: 36px;" aria-label="Öffnen"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="t in tickets"
                :key="t.id"
                class="assona-row"
                tabindex="0"
                @click="openTicket(t.id)"
                @keydown.enter="openTicket(t.id)"
              >
                <td class="assona-ticket">
                  #{{ t.id }}
                  <span v-if="t.is_complaint" class="assona-complaint-badge">Reklamation</span>
                </td>
                <td>{{ formatDate(t.creation_timestamp) }}</td>
                <td>
                  <span class="assona-status" :style="badgeStyle(t)">
                    {{ t.status }}
                    <span v-if="t.status_changed_timestamp" class="assona-status-when">
                      · {{ formatDate(t.status_changed_timestamp) }}
                    </span>
                  </span>
                </td>
                <td class="assona-chevron" aria-hidden="true">›</td>
              </tr>
            </tbody>
          </table>

          <div v-if="totalPages > 1" class="assona-pager">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="page <= 1"
              @click="goToPage(page - 1)"
            >Zurück</button>
            <span class="assona-pager-info">
              Seite {{ page }} / {{ totalPages }} · {{ total }} Tickets
            </span>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="page >= totalPages"
              @click="goToPage(page + 1)"
            >Weiter</button>
          </div>
        </div>
      </template>
    </main>

    <BarcodeScanner ref="scanner" @scanned="onScanned" />
    <Assona24hCreateModal ref="createModal" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import TopNav from '../components/TopNav.vue';
import BarcodeScanner from '../components/BarcodeScanner.vue';
import Assona24hCreateModal from '../components/Assona24hCreateModal.vue';
import { list, type TicketListItem, type TicketListStatusCount } from '../services/tickets';

const router = useRouter();

// Seitenweise vom Server: Liste, Treffer und Status-Zähler kommen aus der
// Datenbank. Der Browser sieht nur eine Seite und kann selbst nicht zählen.
const PAGE_SIZE = 50;

const tickets      = ref<TicketListItem[]>([]);   // aktuelle Seite
const total        = ref(0);                      // Treffer mit aktuellem Filter, alle Seiten
const statusCounts = ref<TicketListStatusCount[]>([]);
const page         = ref(1);

const loading     = ref(true);                    // erstes Laden (ganze Seite)
const loadError   = ref<string | null>(null);
const pageLoading = ref(false);                   // Seiten-, Filter- und Suchwechsel
const pageError   = ref<string | null>(null);

const searchInputValue = ref('');     // aktueller Feldinhalt
const query            = ref('');     // bestätigter Suchbegriff (Enter / Scan)

// null = „Alle"
const activeStatusId = ref<number | null>(null);

const searchInput = useTemplateRef<HTMLInputElement>('searchInput');
const scanner     = useTemplateRef<InstanceType<typeof BarcodeScanner>>('scanner');
const createModal = useTemplateRef<InstanceType<typeof Assona24hCreateModal>>('createModal');

const allCount = computed<number>(() =>
  statusCounts.value.reduce((sum, s) => sum + s.count, 0),
);

const activeStatusName = computed<string>(() =>
  statusCounts.value.find(s => s.status_id === activeStatusId.value)?.status ?? '',
);

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));

// Antworten können sich bei schnellem Klicken überholen - nur die jüngste zählt.
let loadSeq = 0;

async function loadPage(): Promise<void> {
  const seq = ++loadSeq;
  pageLoading.value = true;
  pageError.value   = null;
  try {
    // Gesucht wird über ALLE Tickets, unabhängig vom Status-Filter.
    const result = await list({
      statusId: query.value ? undefined : (activeStatusId.value ?? undefined),
      q:        query.value || undefined,
      page:     page.value,
      perPage:  PAGE_SIZE,
    });
    if (seq !== loadSeq) return;
    tickets.value      = result.items;
    total.value        = result.total;
    statusCounts.value = result.status_counts;
  } catch (e) {
    if (seq !== loadSeq) return;
    tickets.value   = [];
    total.value     = 0;
    pageError.value = e instanceof Error ? e.message : 'Die Liste konnte nicht geladen werden.';
  } finally {
    if (seq === loadSeq) pageLoading.value = false;
  }
}

function goToPage(p: number): void {
  const target = Math.min(totalPages.value, Math.max(1, p));
  if (target === page.value) return;
  page.value = target;
  void loadPage();
}

function openTicket(id: number): void {
  router.push({ name: 'ticket-detail', params: { id } });
}

function setFilter(statusId: number | null): void {
  activeStatusId.value = statusId;
  // Filterwechsel verlässt den Suchmodus.
  searchInputValue.value = '';
  query.value            = '';
  page.value             = 1;
  void loadPage();
}

function badgeStyle(t: TicketListItem): Record<string, string> | undefined {
  if (!t.status_bg && !t.status_fg) return undefined;
  return {
    background: t.status_bg || 'var(--accent-soft, rgba(247, 166, 0, 0.10))',
    color:      t.status_fg || 'var(--accent-hover, #e09600)',
  };
}

function statusStyle(s: TicketListStatusCount): Record<string, string> | undefined {
  if (!s.status_bg && !s.status_fg) return undefined;
  // Soft-Tint-Fläche mit gesättigter Schrift + passender Rahmenfarbe (gleiche Optik wie das Badge).
  return {
    background:  s.status_bg || 'transparent',
    color:       s.status_fg || 'inherit',
    borderColor: s.status_fg || 'currentColor',
  };
}

// „Alle" bleibt neutral, damit es nicht mit den farbigen Status-Buttons konkurriert.
const alleActiveStyle: Record<string, string> = {
  background:  '#e9ecef',
  color:       '#343a40',
  borderColor: '#adb5bd',
};

onMounted(async () => {
  await loadPage();
  // Scheitert schon das erste Laden, gibt es keine Seite, nur die Meldung.
  if (pageError.value) loadError.value = pageError.value;
  loading.value = false;

  // Die Suche ist der Einstieg in die Seite (Scan oder Nummer eintippen), also
  // steht der Cursor dort. Das Feld entsteht erst nach dem Laden, daher nextTick.
  await nextTick();
  searchInput.value?.focus();
});

async function applySearch(): Promise<void> {
  const term = searchInputValue.value.trim();
  if (term === query.value) return;
  query.value = term;
  // Eine Suche läuft über alle Status - das „Alle"-Badge zeigt das an.
  if (term) activeStatusId.value = null;
  page.value  = 1;
  await loadPage();
}

function clearSearch(): void {
  searchInputValue.value = '';
  query.value            = '';
  page.value             = 1;
  void loadPage();
  searchInput.value?.focus();
}

/** „Reparatur hinzufügen": neu angelegtes (oder vorhandenes) Ticket gleich öffnen. */
async function openCreate(): Promise<void> {
  const ticketId = await createModal.value?.open();
  if (ticketId) openTicket(ticketId);
}

function openScanner(): void {
  scanner.value?.open();
}

function onScanned(code: string): void {
  // gescannten Code übernehmen und sofort suchen (entspricht Enter-Bestätigung)
  searchInputValue.value = code;
  void applySearch();
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
}

.assona-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.assona-filters {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  overflow-x: auto;
  padding-bottom: 2px;
}

.assona-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
  white-space: nowrap;
  padding: 6px 14px;
  border: 1px solid #ced4da;
  border-radius: 9999px;
  background: #fff;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.assona-filter:hover {
  border-color: var(--accent, #f7a600);
  color: var(--accent-hover, #e09600);
}

.assona-filter.active {
  border-color: var(--accent, #f7a600);
}

.assona-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4em;
  padding: 0 6px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.10);
  font-size: 12px;
  line-height: 1.6;
}

.assona-search {
  margin-bottom: 1rem;
  max-width: 560px;
}

.assona-scan-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.assona-search-meta {
  margin: 0.5rem 0 0;
  color: #6c757d;
  font-size: 0.875rem;
}

.assona-clear {
  padding: 0;
  vertical-align: baseline;
}

.assona-ticket {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* Reklamation: Folgefall eines früheren Austauschs — fällt in der Liste auf. */
.assona-complaint-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 9999px;
  background: #fff3cd;
  color: #8a5a00;
  border: 1px solid #f0c36d;
  font-size: 12px;
  font-weight: 600;
  vertical-align: 1px;
}

.assona-status {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  color: var(--accent-hover, #e09600);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

/* Zeitpunkt des letzten Statuswechsels — im selben Badge, aber zurückgenommen,
   damit der Status die Hauptaussage bleibt. */
.assona-status-when {
  font-weight: 500;
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

/* Tabelle in Token-Karte; kein extra Rahmen um die Zellen. */
.assona-tablecard {
  padding: 8px 12px;
}

.assona-table {
  margin: 0;
}

/* Kopfzeile klebt beim Scrollen unter der Topnav. */
.assona-table thead th {
  position: sticky;
  top: var(--nav-height, 64px);
  z-index: 1;
  background: var(--surface, #fff);
  border-bottom: 1px solid var(--border);
}

/* Ganze Zeile klickbar (statt „öffnen"-Button pro Zeile). */
.assona-row {
  cursor: pointer;
}
.assona-row:hover td {
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
}
.assona-row:focus-visible {
  outline: 2px solid var(--accent-ring, rgba(247, 166, 0, 0.22));
  outline-offset: -2px;
}

.assona-chevron {
  text-align: right;
  color: var(--muted, #6c757d);
  font-size: 20px;
  line-height: 1;
}

.assona-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 14px 8px 6px;
}
.assona-pager-info {
  color: var(--muted, #6c757d);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}
</style>
